/**
 * Admin Transaction Analytics – dashboard-wt cards, bento stats, chart in card
 */
import React, { useEffect, useState, useRef } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Spinner from '../common/Spinner';
import SimpleChart from './SimpleChart';
import Select from '../common/Select';

const AdminTransactionAnalytics = ({ timeRange = '30d' }) => {
  const { loadAnalytics, analytics, loading } = useAdmin();
  const { showError } = useToast();
  const { formatCurrency, formatNumber, language } = useI18n();
  const [selectedRange, setSelectedRange] = useState(timeRange);
  const [groupBy, setGroupBy] = useState('day');
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        switch (selectedRange) {
          case '24h':
            startDate.setHours(startDate.getHours() - 24);
            break;
          case '7d':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case '30d':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case '90d':
            startDate.setDate(startDate.getDate() - 90);
            break;
          case '1y':
            startDate.setFullYear(startDate.getFullYear() - 1);
            break;
          default:
            startDate.setDate(startDate.getDate() - 30);
        }
        await loadAnalytics({
          metric: 'transactions',
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy: selectedRange === '24h' ? 'hour' : selectedRange === '7d' ? 'day' : groupBy,
        });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des statistiques' : 'Failed to load statistics')
        );
      }
    };
    loadData();
  }, [selectedRange, groupBy, loadAnalytics, showError, language]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (loading && !analytics) {
    return (
      <div className="transactions-wt-loading">
        <div className="dashboard-wt-hero-inner">
          <Spinner size="lg" />
          <p className="mt-4 text-[#a3a3a3]">
            {language === 'fr' ? 'Chargement des statistiques...' : 'Loading statistics...'}
          </p>
        </div>
      </div>
    );
  }

  const payload = analytics?.data?.data ?? analytics?.data ?? analytics ?? {};
  const chartData = payload.data || payload.chartData || [];
  const stats = payload.stats || {};

  const chartDataFormatted = chartData.map((item) => ({
    label: item.label || item.date || item.period || '',
    value: item.count || item.value || item.transactions || 0,
    color: 'from-emerald-500 to-green-600',
  }));

  const statCards = [
    { icon: '📦', label: language === 'fr' ? 'Total' : 'Total', value: formatNumber(stats.total || 0) },
    { icon: '💰', label: language === 'fr' ? 'Montant' : 'Amount', value: formatCurrency(stats.totalAmount || 0, 'XAF') },
    { icon: '✅', label: language === 'fr' ? 'Réussies' : 'Successful', value: formatNumber(stats.successful || 0) },
    { icon: '📈', label: language === 'fr' ? 'Taux' : 'Rate', value: `${(Number(stats.successRate) || 0).toFixed(1)}%` },
  ];

  return (
    <>
      {/* Filters */}
      <div className="dashboard-wt-card mb-6">
        <div className="dashboard-wt-card-header">
          <h2 className="dashboard-wt-card-title">
            📦 {language === 'fr' ? 'Analytique des Transactions' : 'Transaction Analytics'}
          </h2>
        </div>
        <div className="dashboard-wt-card-content">
          <div className="transactions-wt-form-row">
            <div className="transactions-wt-form-select" style={{ maxWidth: 140 }}>
              <Select
                value={selectedRange}
                onChange={(e) => setSelectedRange(e.target.value)}
                options={[
                  { value: '24h', label: '24h' },
                  { value: '7d', label: '7d' },
                  { value: '30d', label: '30d' },
                  { value: '90d', label: '90d' },
                  { value: '1y', label: '1y' },
                ]}
                className="w-full"
              />
            </div>
            {selectedRange !== '24h' && (
              <div className="transactions-wt-form-select" style={{ maxWidth: 140 }}>
                <Select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  options={[
                    { value: 'day', label: language === 'fr' ? 'Jour' : 'Day' },
                    { value: 'week', label: language === 'fr' ? 'Semaine' : 'Week' },
                    { value: 'month', label: language === 'fr' ? 'Mois' : 'Month' },
                  ]}
                  className="w-full"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats bento */}
      <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
        {statCards.map((stat, i) => (
          <div
            key={i}
            className="dashboard-wt-stat-card"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div className="dashboard-wt-stat-icon">
              <span>{stat.icon}</span>
            </div>
            <div className="dashboard-wt-stat-content">
              <div className="dashboard-wt-stat-value">{stat.value}</div>
              <div className="dashboard-wt-stat-label">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="dashboard-wt-card mt-6">
        <div className="dashboard-wt-card-header">
          <h2 className="dashboard-wt-card-title">
            {language === 'fr' ? 'Transactions par Période' : 'Transactions Over Time'}
          </h2>
        </div>
        <div className="dashboard-wt-card-content">
          {chartDataFormatted.length > 0 ? (
            <SimpleChart
              data={chartDataFormatted}
              type="bar"
              height="280px"
              embed
            />
          ) : (
            <div className="dashboard-wt-empty py-12">
              <div className="dashboard-wt-empty-icon">📊</div>
              <h3 className="dashboard-wt-empty-title">
                {language === 'fr' ? 'Aucune transaction sur la période' : 'No transactions in this period'}
              </h3>
              <p className="dashboard-wt-empty-desc">
                {language === 'fr'
                  ? 'Changez la période ou le regroupement pour voir les données.'
                  : 'Change the time range or grouping to see data.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminTransactionAnalytics;
